import type { VideoPolicyRunsItem } from "@unbox/api";

export type PolicyRunAlert =
  | { kind: "activeOtherPolicy"; policyName: string; videoId: string }
  | { kind: "activeSamePolicy"; policyName: string; videoId: string }
  | { kind: "completedRun"; policyName: string; videoId: string; finishedAt: string }
  | { kind: "bulkActiveOtherPolicy"; affected: number; total: number; selectedPolicyName: string }
  | {
      kind: "bulkActiveSamePolicy";
      affected: number;
      total: number;
      remaining: number;
      selectedPolicyName: string;
      selectedPolicyId: string;
    };

type ResolveInput = {
  videoIds: string[];
  runs: VideoPolicyRunsItem[];
  selectedPolicyId: string | undefined;
  policyNameById: Map<string, string>;
};

export type ResolvedPolicyRunAlerts = {
  alerts: PolicyRunAlert[];
  remaining: number;
  startsSecondPolicy: boolean;
};

const EMPTY: ResolvedPolicyRunAlerts = { alerts: [], remaining: 0, startsSecondPolicy: false };

export const resolvePolicyRunAlerts = ({
  videoIds,
  runs,
  selectedPolicyId,
  policyNameById,
}: ResolveInput): ResolvedPolicyRunAlerts => {
  if (!selectedPolicyId || videoIds.length === 0) return { ...EMPTY, remaining: videoIds.length };

  const selectedPolicyName = policyNameById.get(selectedPolicyId) ?? "this policy";
  const selected = runs.filter((run) => videoIds.includes(run.videoId));

  const activeOther = selected.filter((run) => run.activeRun && run.activeRun.policyId !== selectedPolicyId);
  const activeSame = selected.filter((run) => run.activeRun && run.activeRun.policyId === selectedPolicyId);
  const completed = videoIds.length === 1 ? selected.filter((run) => !run.activeRun && run.lastCompletedRun) : [];

  const alerts: PolicyRunAlert[] = [];
  const nameOf = (policyId: string | null) => (policyId && policyNameById.get(policyId)) || "A policy";

  if (activeOther.length === 1) {
    const [run] = activeOther;
    alerts.push({
      kind: "activeOtherPolicy",
      policyName: nameOf(run.activeRun?.policyId ?? null),
      videoId: run.videoId,
    });
  } else if (activeOther.length > 1) {
    alerts.push({
      kind: "bulkActiveOtherPolicy",
      affected: activeOther.length,
      total: videoIds.length,
      selectedPolicyName,
    });
  }

  if (activeSame.length === 1) {
    const [run] = activeSame;
    alerts.push({ kind: "activeSamePolicy", policyName: selectedPolicyName, videoId: run.videoId });
  } else if (activeSame.length > 1) {
    alerts.push({
      kind: "bulkActiveSamePolicy",
      affected: activeSame.length,
      total: videoIds.length,
      remaining: videoIds.length - activeSame.length,
      selectedPolicyName,
      selectedPolicyId,
    });
  }

  if (completed.length === 1) {
    const [run] = completed;
    alerts.push({
      kind: "completedRun",
      policyName: nameOf(run.lastCompletedRun?.policyId ?? null),
      videoId: run.videoId,
      finishedAt: run.lastCompletedRun?.finishedAt ?? "",
    });
  }

  return {
    alerts,
    remaining: videoIds.length - activeSame.length,
    startsSecondPolicy: activeOther.length > 0,
  };
};
